/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * Footer Component
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import logoImg from '@assets/images/vowsera_256x256.png';
import { useTranslation } from 'react-i18next';
import { featuredVendorCategories } from '@data/featuredVendorCategories';
import authService from '@services/authService';
import './Footer.scss';

const Footer = () => {
  const { t } = useTranslation();
  const yr = new Date().getFullYear();
  // Read the brand's actual social URLs from platform settings so the
  // footer stops pointing at bare `https://facebook.com` etc. (audit
  // marketing P1 #1). If no URL is configured for a given network, we
  // hide the icon rather than sending users to the platform's home page.
  const [social, setSocial] = useState({ facebook: '', instagram: '', pinterest: '', twitter: '' });
  useEffect(() => {
    (async () => {
      try {
        const cfg = await authService.getPublicConfig();
        setSocial({
          facebook:  cfg?.brand?.facebook_url  || '',
          instagram: cfg?.brand?.instagram_url || '',
          pinterest: cfg?.brand?.pinterest_url || '',
          twitter:   cfg?.brand?.twitter_url   || '',
        });
      } catch { /* keep defaults, icons stay hidden */ }
    })();
  }, []);

  return (
    <footer className="footer">
      <div className="footer__container">
        <div className="footer__grid">
          {/* Brand */}
          <div>
            <div className="footer__logo">
              <img src={logoImg} alt="VowsEra" style={{ height: 48, verticalAlign: 'middle' }} />
            </div>
            <p className="footer__description">{t('footer.description')}</p>
            <div className="footer__social">
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="footer__social-link">
                  <i className="bi bi-facebook"></i>
                </a>
              )}
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="footer__social-link">
                  <i className="bi bi-instagram"></i>
                </a>
              )}
              {social.pinterest && (
                <a href={social.pinterest} target="_blank" rel="noopener noreferrer" aria-label="Pinterest" className="footer__social-link">
                  <i className="bi bi-pinterest"></i>
                </a>
              )}
              {social.twitter && (
                <a href={social.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="footer__social-link">
                  <i className="bi bi-twitter-x"></i>
                </a>
              )}
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="footer__title">{t('footer.quickLinks')}</h4>
            <ul className="footer__links">
              <li><Link to="/how-it-works">How it works</Link></li>
              <li><Link to="/features">Features</Link></li>
              <li><Link to="/pricing">{t('nav.pricing')}</Link></li>
              <li><Link to="/vendors">{t('nav.vendors')}</Link></li>
              <li><Link to="/venues">{t('nav.venues')}</Link></li>
              <li><Link to="/wedding-planning-guide">Planning guide</Link></li>
              <li><Link to="/wedding-budget-calculator">Budget calculator</Link></li>
              <li><Link to="/wedding-checklist">Wedding checklist</Link></li>
              <li><Link to="/auspicious-wedding-dates">Auspicious wedding dates</Link></li>
              <li><Link to="/wedding-cost">Wedding costs by city</Link></li>
              <li><Link to="/nri-wedding-planning">Marrying in India from abroad</Link></li>
              <li><Link to="/inspiration">Inspiration gallery</Link></li>
              <li><Link to="/find-vendors">Get vendor quotes</Link></li>
              <li><Link to="/blog">Blog</Link></li>
            </ul>
          </div>

          {/* For Professionals */}
          <div>
            <h4 className="footer__title">For Professionals</h4>
            <ul className="footer__links">
              <li><Link to="/for-vendors">List your business</Link></li>
              <li><Link to="/for-venues">Become a venue partner</Link></li>
              <li><Link to="/for-coordinators">For wedding coordinators</Link></li>
              <li><Link to="/register?role=vendor">{t('footer.vendorSignup')}</Link></li>
              <li><Link to="/login">{t('footer.vendorLogin')}</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="footer__title">{t('footer.support')}</h4>
            <ul className="footer__links">
              <li><Link to="/about">{t('nav.about')}</Link></li>
              <li><Link to="/help">{t('footer.helpCenter')}</Link></li>
              <li><Link to="/faq">{t('footer.faq')}</Link></li>
              <li><Link to="/contact">{t('nav.contact')}</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="footer__title">{t('footer.legal')}</h4>
            <ul className="footer__links">
              <li><Link to="/privacy-policy">{t('footer.privacyPolicy')}</Link></li>
              <li><Link to="/terms-of-service">{t('footer.termsOfService')}</Link></li>
              <li><Link to="/security">Security</Link></li>
              <li><Link to="/accessibility">Accessibility</Link></li>
            </ul>
          </div>
        </div>

        {/* SEO: Popular Wedding Cities */}
        <div className="footer__seo-links" style={{borderTop:'1px solid rgba(255,255,255,0.1)',paddingTop:20,marginTop:20}}>
          <h4 className="footer__title">{t('footer.popularCities')}</h4>
          <div style={{display:'flex',flexWrap:'wrap',gap:'6px 14px'}}>
            {[
              'New York','Los Angeles','Chicago','Houston','Miami','Dallas','San Francisco',
              'Austin','Nashville','Charleston','Denver','San Diego','Atlanta','Seattle',
              'Boston','Phoenix','Philadelphia','Las Vegas','Savannah','Portland',
              'New Orleans','Scottsdale','Palm Springs','Napa Valley','Aspen',
              'Maui','Key West','Santa Barbara','Sedona','Lake Tahoe',
              'Washington DC','San Antonio','Minneapolis','Charlotte','Raleigh',
              'Tampa','Orlando','Fort Worth','Honolulu','Salt Lake City',
              'Rio de Janeiro','São Paulo','Cancún'
            ].map(c =>
              <Link key={c} to={`/weddings/${c.toLowerCase().replace(/\s+/g, '-')}`} style={{color:'rgba(255,255,255,0.6)',fontSize:'0.75rem',textDecoration:'none'}}>{t('footer.weddingsIn', { city: c })}</Link>
            )}
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'6px 14px',marginTop:12}}>
            {featuredVendorCategories().map(c =>
              <Link key={c.slug} to={`/vendors/${c.slug}`} style={{color:'rgba(255,255,255,0.6)',fontSize:'0.75rem',textDecoration:'none'}}>{t(c.key)}</Link>
            )}
          </div>
        </div>

        <div className="footer__bottom">
          <p>{t('footer.copyright', { year: yr })}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
